import { For, type JSX, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { LineChart, ShieldCheck, Wallet } from "@fleet/icons";
import { AppBackground } from "@/components/app-background";
import { useI18n } from "@/providers/i18n-provider";

/**
 * CashSync's OWN auth shell — deliberately NOT the shared `AuthLayout` /
 * `AuthCard` glass-card-in-a-split that every clone inherited (#13 de-clone).
 *
 * Where the generic kit framed auth as "a centered glass card floating in a
 * bordered brand panel" (the silhouette that read as a clone across all
 * products), CashSync's identity is a **financial cockpit**:
 *
 *   lg+ (cockpit):
 *   ┌───────────────────────────────┬──────────────────────────┐
 *   │  EDGE-BLEED BRAND PANEL        │  ledger sheet             │
 *   │  ◳ CashSync                    │  Welcome back   · chip ·  │
 *   │  live mini-dashboard hero      │  ─────────────────────    │
 *   │  Big finance promise.          │  Email   [____________]   │
 *   │  • benefit • benefit • benefit │  Pass    [____________]   │
 *   │                                │  [ Sign in ]              │
 *   └───────────────────────────────┴──────────────────────────┘
 *
 *   mobile (stacked): compact brand bar (logo chip + wordmark + chip) → headline
 *   → hairline → flat full-bleed form. No glass card, no rounded bordered panel.
 *
 * The structural differences from the shared template (so two products side by
 * side don't read as the same shell):
 *  - The form is a **flat full-bleed sheet** on the ambient background — there is
 *    no `GlassSurface`/`AuthCard` frame around the fields.
 *  - The brand panel **bleeds to the viewport edge** (a vertical hairline divider
 *    splits it from the form), instead of a rounded `bg-card/40` card-in-a-card.
 *  - The page heading lives in the **form column** (ledger header), not centered
 *    above a card; the brand wordmark is a decorative <p>.
 *
 * ── SCROLL CONTRACT (kit #13 fix, verbatim pattern) ───────────────────────
 * `AppBackground` is `position: fixed; inset: 0; overflow: hidden` and clips its
 * children, so it is used here purely as a backdrop SIBLING. The content lives in
 * its own scroll container: `min-h-[100dvh] overflow-y-auto` (never
 * `h-dvh`/`overflow-hidden`), columns with `min-h-0`, and the brand panel is
 * `hidden … lg:flex` so it collapses BEFORE the form is ever clipped. A tall
 * form (register) or a short landscape viewport stays fully reachable by
 * scrolling.
 */

interface AuthSceneProps {
  /** Eyebrow chip in the ledger header (e.g. "Welcome back"). Optional. */
  eyebrow?: string;
  /** Page headline — the editorial promise for this screen. Optional for bare loading states. */
  headline?: string;
  /** Supporting line under the headline. */
  tagline?: string;
  /**
   * Heading level for the ledger headline. The scene headline IS the page h1 for
   * CashSync's auth (there is no card title). Pass "h2" only if the screen owns
   * its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /**
   * Whether to render the edge-bleed brand panel on lg+. Transactional screens
   * (reset / verify) arrive from an email link and drop the brand to stay calm
   * and focused — the form goes single-column centered.
   */
  showBrand?: boolean;
  /** The form (fields + actions), rendered flat on the ledger sheet. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the form. */
  footer?: JSX.Element;
}

export function AuthScene(props: AuthSceneProps) {
  const showBrand = () => props.showBrand ?? true;
  const isH2 = () => (props.headingLevel ?? "h1") === "h2";

  return (
    <>
      {/* Fixed, decorative backdrop. NEVER the parent that scrolls. */}
      <AppBackground />

      {/* Real scroll container: grows past the viewport and scrolls when the
          form doesn't fit (portrait or short landscape). */}
      <div
        class="relative z-10 h-[100dvh] w-full overflow-x-hidden overflow-y-auto"
        style={{
          "padding-top": "max(0px, env(safe-area-inset-top))",
          "padding-bottom": "max(0px, env(safe-area-inset-bottom))",
          "padding-left": "max(0px, env(safe-area-inset-left))",
          "padding-right": "max(0px, env(safe-area-inset-right))",
        }}
      >
        <div
          class="mx-auto grid min-h-full w-full max-w-6xl grid-cols-1 items-stretch"
          classList={{
            "lg:grid-cols-[1.1fr_minmax(0,30rem)]": showBrand(),
          }}
        >
          {/* Edge-bleed brand panel (lg+ only). A hairline divider — not a card
              border — splits it from the ledger sheet. Collapses before the
              form is clipped. */}
          <Show when={showBrand()}>
            <aside class="hidden min-h-0 border-r border-border/70 px-10 py-12 lg:flex xl:px-14">
              <BrandPanel
                eyebrow={props.eyebrow}
                headline={props.headline}
                tagline={props.tagline}
              />
            </aside>
          </Show>

          {/* Ledger sheet: the form sits flat on the ambient background, no
              glass card. Single-column centered when brand is hidden. */}
          <main
            class="flex min-h-0 w-full flex-col justify-center px-5 py-10 sm:px-8"
            classList={{ "mx-auto max-w-md": !showBrand() }}
          >
            <div class="w-full" classList={{ "mx-auto max-w-md lg:mx-0": showBrand() }}>
              {/* Compact brand bar — visible on mobile (where the panel is
                  hidden) and on the brand-less transactional screens, so the
                  CashSync mark is always present. */}
              <div
                classList={{
                  "lg:hidden": showBrand(),
                }}
              >
                <BrandBar />
                <div class="my-6 h-px w-full bg-border/70" />
              </div>

              {/* Ledger header: eyebrow chip + headline + tagline. Carries h1. */}
              <header class="space-y-3">
                <Show when={props.eyebrow}>
                  <span class="inline-flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent ring-1 ring-accent/20">
                    <span class="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
                    {props.eyebrow}
                  </span>
                </Show>
                <Show when={props.headline}>
                  <div class="space-y-2">
                    <Show
                      when={isH2()}
                      fallback={
                        <h1 class="text-pretty text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                          {props.headline}
                        </h1>
                      }
                    >
                      <h2 class="text-pretty text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                        {props.headline}
                      </h2>
                    </Show>
                    <Show when={props.tagline}>
                      <p class="text-pretty text-base leading-relaxed text-muted-foreground">
                        {props.tagline}
                      </p>
                    </Show>
                  </div>
                </Show>
              </header>

              <div class="my-7 h-px w-full bg-border/70" />

              {/* Form — full-bleed flat sheet, no glass surround. */}
              <div class="w-full">{props.children}</div>

              <Show when={props.footer}>
                <footer class="mt-7 space-y-3">{props.footer}</footer>
              </Show>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}

/**
 * Compact brand bar (logo chip + wordmark) shown on mobile / brand-less screens.
 * Theme-safe: solid accent chip pairs `bg-accent` / `text-accent-foreground`.
 */
function BrandBar() {
  return (
    <Link
      to="/"
      class="group inline-flex items-center gap-2.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      aria-label="CashSync"
    >
      <CoinChip class="h-10 w-10" />
      <span class="text-lg font-bold tracking-tight text-foreground">CashSync</span>
    </Link>
  );
}

/**
 * The lg+ edge-bleed brand panel: wordmark + eyebrow, live-feeling mini
 * dashboard hero, the finance promise, and three benefits. All decorative SVG is
 * `aria-hidden`; copy is real translatable text. The wordmark is a decorative
 * <p> so the ledger header owns the single page h1.
 */
function BrandPanel(props: { eyebrow?: string; headline?: string; tagline?: string }) {
  const { t } = useI18n();

  const benefits = [
    {
      icon: Wallet,
      title: msg`Every account in one balance`,
      body: msg`Wallets, cards and cash roll up into one number you can trust at a glance.`,
    },
    {
      icon: LineChart,
      title: msg`See where the money goes`,
      body: msg`Spending and income turn into clear trends, so the next decision is the easy one.`,
    },
    {
      icon: ShieldCheck,
      title: msg`Private, synced, offline-first`,
      body: msg`Log a transaction with no signal — it syncs securely across your devices later.`,
    },
  ];

  const headline = () => props.headline ?? t(msg`Your money, finally in focus.`);
  const tagline = () =>
    props.tagline ??
    t(
      msg`CashSync turns scattered accounts into one calm, trustworthy balance — every transaction tracked, every trend clear, synced on every device.`,
    );

  return (
    <div class="relative flex min-h-0 w-full flex-col justify-between gap-10 overflow-hidden">
      <BrandBackdrop />

      <div class="relative z-10 flex items-center justify-between gap-4">
        <p class="flex items-center gap-2.5">
          <CoinChip class="h-9 w-9" aria-hidden />
          <span class="text-xl font-bold tracking-tight text-foreground">CashSync</span>
        </p>
        <Show when={props.eyebrow}>
          <span class="rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground shadow-sm ring-1 ring-black/5">
            {props.eyebrow}
          </span>
        </Show>
      </div>

      <DashboardHero />

      <div class="relative z-10 space-y-3">
        <p class="text-balance text-3xl font-bold tracking-tight text-foreground xl:text-4xl">
          {headline()}
        </p>
        <p class="max-w-md text-pretty text-base leading-relaxed text-muted-foreground">
          {tagline()}
        </p>
      </div>

      <ul class="relative z-10 space-y-5">
        <For each={benefits}>
          {(benefit) => (
            <li class="flex items-start gap-4">
              <span
                class="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5"
                aria-hidden="true"
              >
                <benefit.icon class="h-5 w-5" />
              </span>
              <div class="space-y-0.5">
                <p class="font-semibold text-foreground">{t(benefit.title)}</p>
                <p class="text-sm leading-relaxed text-muted-foreground">{t(benefit.body)}</p>
              </div>
            </li>
          )}
        </For>
      </ul>
    </div>
  );
}

/** Solid accent chip + coin-in-a-sync-loop glyph: legible on any theme. */
function CoinChip(props: { class?: string; "aria-hidden"?: boolean }) {
  return (
    <span
      class={`grid place-items-center rounded-xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105 group-active:scale-95 ${props.class ?? ""}`}
      aria-hidden={props["aria-hidden"] ? "true" : undefined}
    >
      <svg class="h-[58%] w-[58%]" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path
          d="M7 12.5a10 10 0 0 1 16-2.2"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M23 6.5v4.2h-4.2"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M25 19.5a10 10 0 0 1-16 2.2"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M9 25.5v-4.2h4.2"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M16 9.5v13M13.4 12.7c0-1.5 1.3-2.4 2.9-2.4s2.9.8 2.9 2.2c0 3-5.8 1.9-5.8 5 0 1.5 1.3 2.4 2.9 2.4s2.9-.9 2.9-2.4"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  );
}

/**
 * Theme-safe mini financial dashboard used as the brand hero on the cockpit
 * panel. A balance KPI card, an ascending cashflow area+bars chart, and a
 * money-flow row — all drawn with theme tokens / opacities, never hardcoded
 * colors. Purely decorative (`aria-hidden`).
 */
function DashboardHero() {
  const bars = [
    { x: 18, h: 26 },
    { x: 42, h: 38 },
    { x: 66, h: 30 },
    { x: 90, h: 52 },
    { x: 114, h: 46 },
    { x: 138, h: 64 },
  ];

  return (
    <div
      class="relative z-10 rounded-2xl border border-border bg-background/50 p-5 shadow-sm backdrop-blur-sm"
      aria-hidden="true"
    >
      <div class="flex items-end justify-between">
        <div class="space-y-1">
          <span class="block h-2 w-16 rounded-full bg-muted-foreground/30" />
          <span class="block h-5 w-28 rounded-md bg-foreground/80" />
        </div>
        <span class="rounded-full bg-accent/15 px-2.5 py-1 text-[11px] font-semibold text-accent">
          + 12.4%
        </span>
      </div>

      <svg
        class="mt-5 h-28 w-full text-accent"
        viewBox="0 0 180 96"
        fill="none"
        preserveAspectRatio="none"
      >
        <line
          x1="0"
          y1="84"
          x2="180"
          y2="84"
          stroke="currentColor"
          stroke-width="1"
          opacity="0.18"
        />
        <line
          x1="0"
          y1="56"
          x2="180"
          y2="56"
          stroke="currentColor"
          stroke-width="1"
          opacity="0.1"
        />
        <line
          x1="0"
          y1="28"
          x2="180"
          y2="28"
          stroke="currentColor"
          stroke-width="1"
          opacity="0.1"
        />
        <path
          d="M6 70 L40 52 L74 58 L108 34 L142 40 L172 18 L172 84 L6 84 Z"
          fill="currentColor"
          opacity="0.12"
        />
        <path
          d="M6 70 L40 52 L74 58 L108 34 L142 40 L172 18"
          stroke="currentColor"
          stroke-width="2.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <For each={bars}>
          {(b) => (
            <rect
              x={b.x}
              y={84 - b.h}
              width="10"
              height={b.h}
              rx="3"
              fill="currentColor"
              opacity="0.22"
            />
          )}
        </For>
      </svg>

      <div class="mt-4 grid grid-cols-2 gap-3">
        <div class="flex items-center gap-2 rounded-xl border border-border bg-card/60 px-3 py-2">
          <span class="grid h-7 w-7 place-items-center rounded-lg bg-accent/15 text-accent">
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 19V5M12 5l-6 6M12 5l6 6"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <div class="space-y-1">
            <span class="block h-1.5 w-10 rounded-full bg-muted-foreground/30" />
            <span class="block h-2.5 w-14 rounded bg-foreground/70" />
          </div>
        </div>
        <div class="flex items-center gap-2 rounded-xl border border-border bg-card/60 px-3 py-2">
          <span class="grid h-7 w-7 place-items-center rounded-lg bg-primary/15 text-primary">
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 5v14M12 19l-6-6M12 19l6-6"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <div class="space-y-1">
            <span class="block h-1.5 w-10 rounded-full bg-muted-foreground/30" />
            <span class="block h-2.5 w-14 rounded bg-foreground/70" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Soft decorative backdrop for the brand panel: blurred accent/primary blobs
 * plus a large coin-sync glyph echoing the logo. Theme tokens / `currentColor`.
 */
function BrandBackdrop() {
  return (
    <div class="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div class="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-accent/10 blur-3xl" />
      <div class="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
      <svg
        class="absolute -bottom-10 -right-8 h-72 w-72 text-accent/10"
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        stroke-width="1"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M7 12.5a10 10 0 0 1 16-2.2" />
        <path d="M23 6.5v4.2h-4.2" />
        <path d="M25 19.5a10 10 0 0 1-16 2.2" />
        <path d="M9 25.5v-4.2h4.2" />
        <path d="M16 9.5v13M13.4 12.7c0-1.5 1.3-2.4 2.9-2.4s2.9.8 2.9 2.2c0 3-5.8 1.9-5.8 5 0 1.5 1.3 2.4 2.9 2.4s2.9-.9 2.9-2.4" />
      </svg>
    </div>
  );
}
